import { startCase } from 'lodash-es';
import { DateTime } from 'luxon';
import type {
  ActivityClientNormalized,
  ActivityNormalized,
  ActivityProjectNormalized,
  ActivityUserNormalized,
} from 'redux/modules/activities.types';
import { AnomalyActivityTypes, RegularActivityTypes } from 'redux/modules/activities.types';
import type { Activity } from 'types/activity';
import type { UserTimeFormat } from 'types/user';
import { prefixHexColor } from 'utils/colors';
import { formatTime, getDaysInRange, isSameYear } from 'utils/date-helper';
import debug from 'utils/debug';
import { fromISO } from 'utils/luxon-helpers';

interface Params {
  activity: ActivityNormalized;
  user: ActivityUserNormalized;
  project: ActivityProjectNormalized | undefined;
  client: ActivityClientNormalized | undefined;
  timeFormat: UserTimeFormat;
}

export const parseActivityType = (
  activityType: string
): RegularActivityTypes | AnomalyActivityTypes => {
  if (activityType in RegularActivityTypes) {
    return activityType as RegularActivityTypes;
  }
  if (activityType in AnomalyActivityTypes) {
    return activityType as AnomalyActivityTypes;
  }
  debug.warn(`Activity type ${activityType} was not found`);
  return RegularActivityTypes.unknown;
};

export const activityFactory = (params: Params): Activity => {
  const { activity, user, project, client, timeFormat } = params;
  const updated = DateTime.fromISO(activity.updated_at);
  const relativeTime = formatTime(activity.updated_at, timeFormat);

  let relativeDay = startCase(updated.toRelativeCalendar() || '');

  const currentDate = DateTime.now().toISODate();
  const activityUpdatedAt = fromISO(activity.updated_at);

  const timeElapsed = getDaysInRange(activityUpdatedAt.toISODate(), currentDate).length;

  if (timeElapsed >= 3) {
    const sameYear = isSameYear(currentDate, activityUpdatedAt.toISODate());
    relativeDay = activityUpdatedAt.toLocaleString({
      ...DateTime.DATE_FULL,
      year: !sameYear ? 'numeric' : undefined,
    });
  }

  // TODO: check if .client and .project below still works!!

  return {
    // omitting activity_type/detail; If message is insufficient, we should
    // build the correct message here based on those details.
    id: activity.id,
    type: parseActivityType(activity.activity_type),
    isAnomaly: activity.anomaly,
    detail: activity.detail,
    message: activity.message,
    // Not currently in API, but will be.
    notable: !!activity.notable,
    // Should we need budget information, use projectWithBudgetFactory after
    // passing the "real" project from getProjectActivities selector.
    project: {
      id: project ? project.id : null,
      name: project ? project.name : '',
      color: prefixHexColor(project?.color),
      isDeleted: project ? activity.entity_deleted : null,
      client: {
        id: project && project.client ? project.client.id : null,
        name: project && project.client ? project.client.name : '',
        color: prefixHexColor(project?.client?.color) || '#ccc',
      },
    },
    client: {
      id: client ? client.id : null,
      name: client ? client.name : '',
      isArchived: client ? !client.active : null,
      color: prefixHexColor(client?.color) || '#ccc',
    },
    user: {
      id: user.id,
      name: user.name,
      avatar: user.avatar ? user.avatar.medium_retina : null,
    },
    url: activity.url,
    relativeTime,
    relativeDay,
    date: activity.updated_at,
  };
};
